/**
 * Field dropdown with compatibility marks, measurement context, and hints.
 */

import { CheckCircle2, XCircle } from 'lucide-react';
import { useMemo } from 'react';

import { MeasurementHint } from './measurement-hint';

import {
    Select,
    SelectContent,
    SelectItem,
    SelectSeparator,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import type {
    FieldCompatibilityMap,
    IntroductionSide,
    MeasurementLookup,
    ICatalogField,
    Side,
} from '@/workflows/vascular/common/catalog';
import { getMeasureIds, computeFieldHint } from '@/workflows/vascular/common/catalog';

interface FieldDropdownProps {
    field: ICatalogField;
    currentValue: string | number | null;
    compatibilityMap: FieldCompatibilityMap;
    measurementLookup: MeasurementLookup;
    side: Side;
    /** Introduction side — used to resolve L/R measures on parts with side='none'. */
    introductionSide?: IntroductionSide;
    onChange: (value: string | number | null) => void;
    /** Cascade mode: only show compatible values, no tick/X icons. */
    cascadeMode?: boolean;
    /** Whether this dropdown is disabled (e.g., upstream fields not yet selected). */
    disabled?: boolean;
}

export function FieldDropdown({
    field,
    currentValue,
    compatibilityMap,
    measurementLookup,
    side,
    introductionSide,
    onChange,
    cascadeMode,
    disabled,
}: FieldDropdownProps) {
    const sortedOptions = useMemo(() => {
        const entries = [...compatibilityMap.entries()];
        if (cascadeMode) {
            // Cascade mode: only show compatible values, sorted by value
            return entries
                .filter(([, status]) => status === 'compatible')
                .sort((a, b) => {
                    if (typeof a[0] === 'number' && typeof b[0] === 'number') return a[0] - b[0];
                    return String(a[0]).localeCompare(String(b[0]));
                });
        }
        // Icon-marking mode: compatible first, then by value
        return entries.sort((a, b) => {
            if (a[1] !== b[1]) return a[1] === 'compatible' ? -1 : 1;
            if (typeof a[0] === 'number' && typeof b[0] === 'number') return a[0] - b[0];
            return String(a[0]).localeCompare(String(b[0]));
        });
    }, [compatibilityMap, cascadeMode]);

    const hint = useMemo(
        () =>
            field.info && currentValue != null && typeof currentValue === 'number'
                ? computeFieldHint(field, currentValue, measurementLookup, side, introductionSide)
                : null,
        [field, currentValue, measurementLookup, side, introductionSide]
    );

    // Resolve measurement values to display at the top of the dropdown
    const measurementEntries = useMemo(() => {
        const ids = getMeasureIds(field, side, introductionSide);
        if (ids.length === 0) return [];
        return ids
            .map((id) => {
                const m = measurementLookup[id];
                if (!m || m.value == null) return null;
                return { label: m.label, value: m.value, unit: m.unit };
            })
            .filter((e): e is NonNullable<typeof e> => e != null);
    }, [field, side, introductionSide, measurementLookup]);

    const unit = field.unit ?? (field.type === 'number' ? 'mm' : '');

    return (
        <div className="flex items-center gap-1.5">
            <label className="w-28 shrink-0 text-[10px] text-muted-foreground truncate" title={field.label}>
                {field.label}
                {unit && <span className="ml-0.5 opacity-60">({unit})</span>}
            </label>
            <Select
                value={currentValue != null ? String(currentValue) : ''}
                onValueChange={(v) => {
                    if (!v) {
                        onChange(null);
                        return;
                    }
                    onChange(field.type === 'number' ? Number(v) : v);
                }}
                disabled={disabled}
            >
                <SelectTrigger className="h-7 flex-1 text-xs">
                    <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                    {measurementEntries.length > 0 && (
                        <>
                            {measurementEntries.map((m) => (
                                <div
                                    key={m.label}
                                    className="px-2 py-1 text-[10px] text-muted-foreground font-mono"
                                >
                                    {m.label}: {m.value.toFixed(1)} {m.unit}
                                </div>
                            ))}
                            <SelectSeparator />
                        </>
                    )}
                    {sortedOptions.map(([value, status]) => (
                        <SelectItem key={String(value)} value={String(value)} className="text-xs">
                            <span className="flex items-center gap-1">
                                {!cascadeMode &&
                                    (status === 'compatible' ? (
                                        <CheckCircle2 className="h-3 w-3 text-green-500 shrink-0" />
                                    ) : (
                                        <XCircle className="h-3 w-3 text-red-400 shrink-0" />
                                    ))}
                                {String(value)}
                            </span>
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
            <MeasurementHint hint={hint} />
        </div>
    );
}
